import { Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../utils";

import { t } from "i18next";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import GlobalButton from "../../../components/CustomButton";
import PackageTable from "../../../components/Tables/PackageTable";
import useDebounce from "../../../hooks/useDebounce";
import { setDetailIds } from "../../../lib/slices/detailIdsSlice";
import {
  setPackageCurrentPage,
  setPackagePageLimit,
  setPackageTotalCount,
  setPackageTotalPages,
} from "../../../lib/slices/packagePageSlice";
import {
  deleteBasePackage,
  exportBasePackages,
  getAllBasePackages,
  updateBasePackage,
} from "../../../services/basePackagesFn";
import { BasePackagesFilters } from "./Filters";
import { useTranslation } from "react-i18next";
import { deleteConfirmAlert } from "../../../components/Alert";
import { CSVLink } from "react-csv";
import { APP_ICONS } from "../../../assets";
import moment from "moment";
import {
  CURRENCY,
  DEFAULT_VAT_PERCENTAGE,
  PRODUCT_USAGE_TYPE,
} from "../../../constants/Variables";

const BasePackagesList = ({ handleChangeTab }) => {
  const csvLinkRef = useRef(null);
  const dispatch = useDispatch();
  const [allProducts, setAllProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportData, setExportData] = useState([]);
  const { t } = useTranslation(["products", "common"]);

  const userData = useSelector((state) => state.userData.user);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const detailIds = useSelector((state) => state.detailIdReducer.detailIds);
  const pagination = useSelector((state) => state.packagePaginationReducer);
  const settings = useSelector((state) => state.settingsReducer.settings);

  const debouncedSearchTerm = useDebounce(
    searchKeys.packageByName,
    1000,
    true,
    () => {
      dispatch(setPackageCurrentPage(1));
    },
  );
  // const canEditDelete =
  //   userData?.adminId === (searchKeys.vendorsForPackage || userData?.adminId);

  const headerData = [
    "createdAt",
    "store",
    "nameImage",
    "solutionType",
    "category",
    "subCategory",
    "subSubCategory",
    "packagePrice",
    "packageEffectivePrice",
    "vat",
    "packageVatAmount",
    "packagePriceAfterVat",
    "orderAllowed",
    "totalOrders",
    "rating",
    // ...(canEditDelete ? ["verified", "status"] : []),
  ];
  const tableRows = [
    "created_date",
    "store_name",
    "name_image",
    "solution_type",
    "category_name",
    "sub_category_name",
    "sub_sub_category_name",
    "package_price",
    "package_effective_price",
    "app_vat",
    "package_vat_amount",
    "package_price_after_vat",
    "order_allowed",
    "totalOrders",
    "rating_in_number",
    "menu_actions",
  ];

  const fetchchedAllPackages = async (
    search,
    categoryId,
    vendorId,
    page,
    limit,
  ) => {
    const params = {
      search: search || "",
      categoryId: categoryId && categoryId !== "All" ? categoryId : "",
      page,
      limit,
      supplierId: vendorId === "all" ? "" : vendorId,
    };

    try {
      setIsLoading(true);
      const response = await getAllBasePackages(params);
      if (response?.data?.status === "success") {
        const data = response?.data?.data?.data;
        const pagination = response?.data?.data?.pagination;
        setAllProducts(data);
        dispatch(setPackagePageLimit(pagination.limit));
        dispatch(setPackageCurrentPage(pagination.page));
        dispatch(setPackageTotalCount(pagination.total));
        dispatch(setPackageTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchchedAllPackages(
        debouncedSearchTerm,
        searchKeys.ecomCategoryIdForPackage,
        searchKeys.vendorsForPackage,
        pagination.page,
        pagination.limit,
      );
    }
  }, [
    userData,
    debouncedSearchTerm,
    searchKeys.ecomCategoryIdForPackage,
    searchKeys.vendorsForPackage,
    pagination.page,
    pagination.limit,
  ]);

  const handleChangeStatus = async (e, row, key = "status") => {
    try {
      setIsLoading(true);
      const response = await updateBasePackage(row?._id, { [key]: e });
      if (response?.data?.status === "success") {
        fetchchedAllPackages(
          debouncedSearchTerm,
          searchKeys.ecomCategoryIdForPackage,
          searchKeys.vendorsForPackage,
          pagination.page,
          pagination.limit,
        );
        handleOpenSnackBar("success", response?.data?.message);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (row) => {
    handleChangeTab("add_edit_package");
    dispatch(setDetailIds({ ...detailIds, packageId: row?._id }));
  };

  // Export data for base packages
  const prepareExportData = async () => {
    try {
      setExportLoading(true);
      const query = {
        supplierId:
          searchKeys.vendorsForPackage === "all"
            ? ""
            : searchKeys.vendorsForPackage,
      };
      const response = await exportBasePackages(query);
      if (response?.data?.status === "success") {
        const data = response?.data?.data;
        const formattedData = [];
        data.forEach((item) => {
          // This is calculated for the base packages
          const finalPackagePrice = item.effectivePackagePrice;

          // This is calculated for the base packages
          const packageVat = calculateVat(
            finalPackagePrice,
            settings?.tax || DEFAULT_VAT_PERCENTAGE,
          );

          // This is calculated for the base packages
          const finalPriceAfterVat = roundTo2Decimal(
            finalPackagePrice + packageVat,
          );
          formattedData.push({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
            "Object Id": item._id || "-",
            "Slug In (Ro)": item.mlSlug?.ro || "-",
            "Slug In (En)": item.mlSlug?.en || "-",
            "Store Name": item?.createdBy?.businessName || "-",
            "Cover Image": item.coverImage || "-",
            Images: item.images?.map((image) => image).join(", ") || "-",
            "Name In (Ro)": item.name?.ro || "-",
            "Name In (En)": item.name?.en || "-",
            "Solution Type (Ro)":
              item.solutionType
                ?.map(
                  (type) =>
                    PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label?.ro,
                )
                .filter(Boolean)
                .join(", ") || "-",
            "Solution Type (En)":
              item.solutionType
                ?.map(
                  (type) =>
                    PRODUCT_USAGE_TYPE.find((u) => u.value === type)?.label?.en,
                )
                .filter(Boolean)
                .join(", ") || "-",
            "Category (Ro)": item.category?.name?.ro || "-",
            "Category (En)": item.category?.name?.en || "-",
            "Sub Category (Ro)":
              item.subCategory?.map((sub) => sub.name?.ro).join(", ") || "-",
            "Sub Category (En)":
              item.subCategory?.map((sub) => sub.name?.en).join(", ") || "-",
            "Sub Sub Category (Ro)":
              item.subSubCategory?.map((sub) => sub.name?.ro).join(", ") || "-",
            "Sub Sub Category (En)":
              item.subSubCategory?.map((sub) => sub.name?.en).join(", ") || "-",
            Price: `${item.packagePrice?.toFixed(2)} ${CURRENCY}`,
            "Effective Price": `${item.effectivePackagePrice?.toFixed(2)} ${CURRENCY}`,
            VAT: `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%`,
            "VAT Amount": `${packageVat.toFixed(2)} ${CURRENCY}`,
            "Price After VAT": `${finalPriceAfterVat.toFixed(2)} ${CURRENCY}`,
            "Order Allowed": item.isAllowedOrder ? "Yes" : "No",
            "Total Orders": item.totalOrders,
            Status: item.status ? "Active" : "Inactive",
            Verified: item.isVerified ? "Yes" : "No",
            Rating: item.rating?.toFixed(0) || "-",
          });
        });

        setExportData(formattedData);

        // Data set hone ke baad manually click trigger karo
        setTimeout(() => {
          csvLinkRef.current?.link?.click();
        }, 100);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setExportLoading(false);
    }
  };

  const renderHeaderAction = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={{ sm: "row" }}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        {/* <GlobalButton
          variant={"contained"}
          btnText={t("addPackage")}
          handleClickBtn={() => {
            handleChangeTab("add_edit_package");
            dispatch(setDetailIds({ ...detailIds, packageId: "" }));
          }}
          styleProps={{
            width: "auto",
            height: 40,
          }}
        /> */}
          <CSVLink
            ref={csvLinkRef}
            data={exportData}
            filename={"base_packages.csv"}
            className="csv-link"
            target="_blank"
            style={{ display: "none" }}
          />

          <GlobalButton
            variant={"export_button"}
            btnText={"export"}
            startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
            disabled={exportLoading}
            handleClickBtn={prepareExportData}
          />
      </Stack>
    );
  };

  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setPackageCurrentPage(page));
  };

  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setPackagePageLimit(limit));
    dispatch(setPackageCurrentPage(1));
  };

  const handleDeleteBasePackage = async (row) => {
    deleteConfirmAlert({
      title: t("deleteConfirmation", { ns: "common" }),
      text: t("deleteConfirmationText", { ns: "common" }),
      confirmButtonText: t("delete", { ns: "common" }),
      cancelButtonText: t("cancel", { ns: "common" }),
    }).then(async (result) => {
      if (result) {
        try {
          setIsLoading(true);
          const response = await deleteBasePackage(row?._id);
          if (response?.data?.status === "success") {
            fetchchedAllPackages(
              debouncedSearchTerm,
              searchKeys.ecomCategoryIdForPackage,
              searchKeys.vendorsForPackage,
              pagination.page,
              pagination.limit,
            );
            handleOpenSnackBar("success", response?.data?.message);
          } else {
            handleOpenSnackBar("error", response?.data?.message);
          }
        } catch (error) {
          handleOpenSnackBar("error", error?.message);
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const allProductsData = useMemo(() => {
    return (
      <PackageTable
        minWidth={2800}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allProducts}
        handleChangeStatus={(e, row) => handleChangeStatus(e, row, "status")}
        handleUpdateVerification={(e, row) =>
          handleChangeStatus(e, row, "isVerified")
        }
        showEdit={true}
        handleEdit={handleEdit}
        showDelete={false}
        handleDelete={handleDeleteBasePackage}
        paginatedData={pagination}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        showActions={true}
        handleUpdatePromotedStatus={(e, row) =>
          handleChangeStatus(e, row, "isPromoted")
        }
      />
    );
  }, [allProducts]);

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("basePackagesList")}
        subtitle={t("basePackagesListInstruction")}
        headerActions={renderHeaderAction()}
        searchActions={<BasePackagesFilters />}
      >
        {allProductsData}
        {allProducts?.length === 0 && isLoading === false && (
          <Typography variant="h5" fontWeight={600} textAlign={"center"}>
            {t("noDataFound", { ns: "common" })}!
          </Typography>
        )}
      </PaperContainer>
    </>
  );
};

export default BasePackagesList;
